import { create } from 'zustand';
import { AxiosError } from 'axios';
import {
  ContactMessage,
  CreateContactPayload,
  submitContactFormApi,
  getAllContactMessagesApi,
  replyToContactMessageApi
} from '../api/support.api';

interface SupportState {
  messages: ContactMessage[];
  loading: boolean;
  error: string | null;
  submitContactForm: (payload: CreateContactPayload) => Promise<void>;
  fetchAllContactMessages: () => Promise<void>;
  replyToContactMessage: (id: number, replyText: string) => Promise<void>;
}

export const useSupportStore = create<SupportState>((set, get) => ({
  messages: [],
  loading: false,
  error: null,

  submitContactForm: async (payload) => {
    set({ loading: true, error: null });
    try {
      await submitContactFormApi(payload);
    } catch (err) {
      const error = err as AxiosError<{ error?: string }>;
      const msg =
        error.response?.data?.error || 'Failed to submit contact request';
      set({ error: msg });
      throw new Error(msg); // 🚀 Fixed: Wrapped inside an Error object
    } finally {
      set({ loading: false });
    }
  },

  fetchAllContactMessages: async () => {
    set({ loading: true, error: null });
    try {
      const data = await getAllContactMessagesApi();
      set({ messages: data || [] });
    } catch (err) {
      const error = err as AxiosError<{ error?: string }>;
      const msg = error.response?.data?.error || 'Failed to fetch messages';
      set({ error: msg });
      throw new Error(msg); // 🚀 Fixed: Wrapped inside an Error object
    } finally {
      set({ loading: false });
    }
  },

  replyToContactMessage: async (id, replyText) => {
    set({ loading: true, error: null });
    try {
      await replyToContactMessageApi(id, replyText);

      const updatedMessages = get().messages.map((msg) =>
        Number(msg.id) === id ? { ...msg, status: 'RESOLVED' as const } : msg
      );

      set({ messages: updatedMessages });
    } catch (err) {
      const error = err as AxiosError<{ error?: string }>;
      const msg =
        error.response?.data?.error || 'Failed to send response email.';
      set({ error: msg });
      throw new Error(msg); // 🚀 Fixed: Wrapped inside an Error object
    } finally {
      set({ loading: false });
    }
  }
}));
